export * from "./core";
export * from "./palettes";
import {
  colors,
  spacing,
  borderRadius,
  typography,
  shadows,
  animation,
} from "./core";
import * as zendirPalette from "./palettes/zendir";
import * as astroPalette from "./palettes/astro";
import { tailwindScales } from "./palettes/tailwind";
import type { ThemeTokensBase } from "../react/theme/types";
import {
  astroThemeDark,
  astroThemeLight,
  hybridTheme,
  purpleHueTheme,
  nebulaTheme,
  transparentTheme,
  transparentMinimalTheme,
} from "./themes";

// ============================================================================
// CSS Custom Properties generator (reference / programmatic use)
//
// NOTE: src/tokens/tokens.css is hand-maintained (see its header) — the build
// does NOT overwrite it from here. These generators remain available for
// consumers that want to emit the same variable set programmatically (SSR
// style injection, tooling) and as the canonical reference for what
// tokens.css should contain.
//
// Two callable surfaces:
//   • generateCSSVariables(mode)         — single :root block for one mode
//                                          (legacy, kept for backward-compat)
//   • generateCSSStylesheet()            — full tokens.css equivalent with
//                                          mode-invariant block + per-theme
//                                          [data-theme="…"] overrides. This is
//                                          what scripts/build-tokens.ts uses.
// ============================================================================

/**
 * Build a CSS rule body for the mode-invariant tokens (status, spacing,
 * radius, typography, shadows, animation). These don't change between
 * light/dark or across themes — they're shared.
 */
function buildInvariantBlock(): string {
  return [
    "  /* ============================================================",
    "   * STATUS COLORS (Astro UX Compliant)",
    "   * ============================================================ */",
    `  --zendir-status-critical: ${colors.status.critical};`,
    `  --zendir-status-serious: ${colors.status.serious};`,
    `  --zendir-status-caution: ${colors.status.caution};`,
    `  --zendir-status-normal: ${colors.status.normal};`,
    `  --zendir-status-standby: ${colors.status.standby};`,
    `  --zendir-status-off: ${colors.status.off};`,
    "",
    "  /* Light-theme status fill colors */",
    `  --zendir-status-critical-light: ${colors.statusLightFill.critical};`,
    `  --zendir-status-serious-light: ${colors.statusLightFill.serious};`,
    `  --zendir-status-caution-light: ${colors.statusLightFill.caution};`,
    `  --zendir-status-normal-light: ${colors.statusLightFill.normal};`,
    `  --zendir-status-standby-light: ${colors.statusLightFill.standby};`,
    `  --zendir-status-off-light: ${colors.statusLightFill.off};`,
    "",
    "  /* Light-theme status border colors */",
    `  --zendir-status-critical-border: ${colors.statusLightBorder.critical};`,
    `  --zendir-status-serious-border: ${colors.statusLightBorder.serious};`,
    `  --zendir-status-caution-border: ${colors.statusLightBorder.caution};`,
    `  --zendir-status-normal-border: ${colors.statusLightBorder.normal};`,
    `  --zendir-status-standby-border: ${colors.statusLightBorder.standby};`,
    `  --zendir-status-off-border: ${colors.statusLightBorder.off};`,
    "",
    "  /* ============================================================",
    "   * SPACING",
    "   * ============================================================ */",
    `  --zendir-spacing-xs: ${spacing.xs};`,
    `  --zendir-spacing-sm: ${spacing.sm};`,
    `  --zendir-spacing-md: ${spacing.md};`,
    `  --zendir-spacing-lg: ${spacing.lg};`,
    `  --zendir-spacing-xl: ${spacing.xl};`,
    `  --zendir-spacing-xxl: ${spacing.xxl};`,
    "",
    "  /* ============================================================",
    "   * BORDER RADIUS",
    "   * ============================================================ */",
    `  --zendir-radius-none: ${borderRadius.none};`,
    `  --zendir-radius-sm: ${borderRadius.sm};`,
    `  --zendir-radius-md: ${borderRadius.md};`,
    `  --zendir-radius-lg: ${borderRadius.lg};`,
    `  --zendir-radius-xl: ${borderRadius.xl};`,
    `  --zendir-radius-full: ${borderRadius.full};`,
    "",
    "  /* ============================================================",
    "   * TYPOGRAPHY",
    "   * Public Sans leads body, Sora leads headings, Roboto is the",
    "   * AstroUXDS-compliant fallback. OS-native fonts complete the chain.",
    "   * @see https://www.astrouxds.com/foundations/typography/",
    "   * ============================================================ */",
    `  --zendir-font-primary: ${typography.fontFamily.primary};`,
    `  --zendir-font-heading: ${typography.fontFamily.heading};`,
    `  --zendir-font-mono: ${typography.fontFamily.mono};`,
    "",
    "  /* Font sizes (rem-based, AstroUXDS scale) */",
    `  --zendir-font-size-micro: ${typography.fontSize.micro};`,
    `  --zendir-font-size-xxs: ${typography.fontSize.xxs};`,
    `  --zendir-font-size-xs: ${typography.fontSize.xs};`,
    `  --zendir-font-size-sm: ${typography.fontSize.sm};`,
    `  --zendir-font-size-base: ${typography.fontSize.base};`,
    `  --zendir-font-size-md: ${typography.fontSize.md};`,
    `  --zendir-font-size-lg: ${typography.fontSize.lg};`,
    `  --zendir-font-size-xl: ${typography.fontSize.xl};`,
    `  --zendir-font-size-xxl: ${typography.fontSize.xxl};`,
    `  --zendir-font-size-xxxl: ${typography.fontSize.xxxl};`,
    `  --zendir-font-size-display: ${typography.fontSize.display};`,
    "",
    "  /* Font weights — AstroUXDS supports only 300/400/500/700 */",
    `  --zendir-font-weight-light: ${typography.fontWeight.light};`,
    `  --zendir-font-weight-normal: ${typography.fontWeight.normal};`,
    `  --zendir-font-weight-medium: ${typography.fontWeight.medium};`,
    // Astro maps semibold onto medium (500); kept so consumers referencing
    // the semibold variable resolve rather than fall back to browser default.
    `  --zendir-font-weight-semibold: ${typography.fontWeight.semibold};`,
    `  --zendir-font-weight-bold: ${typography.fontWeight.bold};`,
    "",
    "  /* Line heights */",
    `  --zendir-line-height-tight: ${typography.lineHeight.tight};`,
    `  --zendir-line-height-normal: ${typography.lineHeight.normal};`,
    `  --zendir-line-height-relaxed: ${typography.lineHeight.relaxed};`,
    "",
    "  /* ============================================================",
    "   * SHADOWS",
    "   * ============================================================ */",
    `  --zendir-shadow-none: ${shadows.none};`,
    `  --zendir-shadow-sm: ${shadows.sm};`,
    `  --zendir-shadow-md: ${shadows.md};`,
    `  --zendir-shadow-lg: ${shadows.lg};`,
    `  --zendir-shadow-xl: ${shadows.xl};`,
    `  --zendir-shadow-glow: ${shadows.glow};`,
    "",
    "  /* ============================================================",
    "   * ANIMATION",
    "   * ============================================================ */",
    `  --zendir-animation-fast: ${animation.fast};`,
    `  --zendir-animation-normal: ${animation.normal};`,
    `  --zendir-animation-slow: ${animation.slow};`,
  ].join("\n");
}

/** Build the mode-specific block (background + text + border + accent + semantic). */
function buildModeBlock(theme: ThemeTokensBase): string {
  const bg = theme.colors.background;
  const txt = theme.colors.text;
  const bdr = theme.colors.border;
  const accent = theme.colors.accent;
  const semantic = theme.colors.semantic;
  return [
    `  --zendir-bg-base: ${bg.base};`,
    `  --zendir-bg-surface: ${bg.surface};`,
    `  --zendir-bg-elevated: ${bg.elevated};`,
    `  --zendir-bg-overlay: ${bg.overlay};`,
    "",
    `  --zendir-text-primary: ${txt.primary};`,
    `  --zendir-text-secondary: ${txt.secondary};`,
    `  --zendir-text-tertiary: ${txt.tertiary};`,
    `  --zendir-text-muted: ${txt.muted || txt.tertiary};`,
    "",
    `  --zendir-border-default: ${bdr.default};`,
    `  --zendir-border-muted: ${bdr.muted};`,
    `  --zendir-border-focus: ${bdr.focus};`,
    "",
    // Accent + semantic colors vary per theme (nebula is ultraviolet,
    // hybrid is purple, astro is blue) — they must live in the theme
    // block, not the invariant block, or every non-astro theme renders
    // the wrong accent.
    `  --zendir-accent-primary: ${accent.primary};`,
    `  --zendir-accent-secondary: ${accent.secondary};`,
    `  --zendir-accent-tertiary: ${accent.tertiary};`,
    "",
    `  --zendir-semantic-success: ${semantic.success};`,
    `  --zendir-semantic-warning: ${semantic.warning};`,
    `  --zendir-semantic-error: ${semantic.error};`,
    `  --zendir-semantic-info: ${semantic.info};`,
  ].join("\n");
}

/**
 * Emit every primitive palette stop as a CSS variable. Mode-invariant by
 * definition — primitives never change with theme. Naming:
 *   Zendir scales   --zendir-eb-500 / --zendir-pb-500 / --zendir-uv-500 /
 *                   --zendir-neutral-500
 *   Astro ramps     --zendir-astro-red-500 … --zendir-astro-cyan-900
 *   Tailwind v4.2   --zendir-tw-red-500 … --zendir-tw-neutral-950
 */
function buildPaletteBlock(): string {
  const lines: string[] = [];
  const emit = (prefix: string, scale: Record<string, string>) => {
    for (const [stop, hex] of Object.entries(scale)) {
      lines.push(`  --zendir-${prefix}-${stop}: ${hex};`);
    }
  };

  lines.push("  /* Zendir Colour System */");
  emit("eb", zendirPalette.electricBlue);
  emit("pb", zendirPalette.prussianBlue);
  emit("uv", zendirPalette.ultraviolet);
  emit("neutral", zendirPalette.neutral);
  lines.push("");
  lines.push("  /* Astro UXDS status ramps */");
  emit("astro-red", astroPalette.red);
  emit("astro-amber", astroPalette.amber);
  emit("astro-yellow", astroPalette.yellow);
  emit("astro-green", astroPalette.green);
  emit("astro-cyan", astroPalette.cyan);
  lines.push("");
  lines.push("  /* Tailwind CSS v4.2 */");
  for (const [name, scale] of Object.entries(tailwindScales)) {
    emit(`tw-${name.toLowerCase()}`, scale as Record<string, string>);
  }
  return lines.join("\n");
}

/**
 * Single-mode generator. Emits one `:root { … }` block with the
 * mode-invariant tokens AND the chosen mode's bg/text/border.
 *
 * Useful for runtime injection — `<style>` tags written by SSR helpers
 * that target a single mode. The full stylesheet generator below is what
 * ships to consumers.
 */
export function generateCSSVariables(mode: "dark" | "light" = "dark"): string {
  return [
    ":root {",
    buildInvariantBlock(),
    "",
    `  /* ${mode === "dark" ? "Dark" : "Light"}-theme color overrides */`,
    buildModeBlock(mode === "dark" ? hybridTheme : astroThemeLight),
    "}",
  ].join("\n");
}

/**
 * Full stylesheet generator. Emits the complete tokens.css equivalent —
 * mode-invariant `:root` block, the primitive palette block, plus per-theme
 * `[data-theme="…"]` overrides. This is what scripts/build-tokens.ts writes
 * to src/tokens/tokens.css.
 */
export function generateCSSStylesheet(): string {
  return [
    "/**",
    " * @zendir/ui — CSS Design Tokens",
    " *",
    " * AUTO-GENERATED from src/tokens/index.ts. DO NOT EDIT BY HAND.",
    " * Regenerate with: npm run build:tokens",
    " *",
    " * Usage:",
    " *   @import '@zendir/ui/tokens/css';",
    " *   .my-component { background: var(--zendir-bg-surface); }",
    " */",
    "",
    ":root {",
    buildInvariantBlock(),
    "}",
    "",
    "/* ============================================================",
    " * PRIMITIVE PALETTES (Layer 0 — src/tokens/palettes)",
    " * Raw scales; prefer the semantic tokens above for UI surfaces.",
    " * ============================================================ */",
    ":root {",
    buildPaletteBlock(),
    "}",
    "",
    "/* ============================================================",
    " * ZEN (HYBRID) THEME (default)",
    " * ============================================================ */",
    ":root,",
    '[data-theme="hybrid"],',
    '[data-theme="dark"] {',
    buildModeBlock(hybridTheme),
    "}",
    "",
    "/* ============================================================",
    " * ASTRO THEME",
    " * ============================================================ */",
    '[data-theme="astro"] {',
    buildModeBlock(astroThemeDark),
    "}",
    "",
    '[data-theme="astro"][data-mode="light"],',
    '[data-theme="hybrid"][data-mode="light"],',
    '[data-theme="nebula"][data-mode="light"],',
    '[data-theme="purple-hue"][data-mode="light"],',
    '[data-theme="light"] {',
    buildModeBlock(astroThemeLight),
    "}",
    "",
    "/* ============================================================",
    " * NEBULA THEME",
    " * ============================================================ */",
    '[data-theme="nebula"] {',
    buildModeBlock(nebulaTheme),
    "}",
    "",
    "/* ============================================================",
    " * PURPLE HUE THEME",
    " * ============================================================ */",
    '[data-theme="purple-hue"] {',
    buildModeBlock(purpleHueTheme),
    "}",
    "",
    "/* ============================================================",
    " * TRANSPARENT THEMES",
    " * ============================================================ */",
    '[data-theme="transparent"], [data-theme="transparent-bold"] {',
    buildModeBlock(transparentTheme),
    "}",
    "",
    '[data-theme="transparent-minimal"] {',
    buildModeBlock(transparentMinimalTheme),
    "}",
    "",
  ].join("\n");
}

export default {
  colors,
  spacing,
  borderRadius,
  typography,
  shadows,
  animation,
  generateCSSVariables,
  generateCSSStylesheet,
};
